import { useFadeIn } from '../../../hooks/useFadeIn';
import { useLang } from '../../../context/LanguageContext';

const steps = [
  {
    number: '01',
    icon: 'ri-message-3-line',
    titleRo: 'Contactați-ne',
    titleRu: 'Свяжитесь с нами',
    descRo: 'Scrieți-ne pe WhatsApp. Echipa noastră răspunde în maxim 30 de minute, 7 zile din 7.',
    descRu: 'Напишите нам в WhatsApp. Мы отвечаем в течение 30 минут, 7 дней в неделю.',
    detailRo: 'WhatsApp · Telefon',
    detailRu: 'WhatsApp · Телефон',
  },
  {
    number: '02',
    icon: 'ri-map-pin-user-line',
    titleRo: 'Evaluare Gratuită',
    titleRu: 'Бесплатная оценка',
    descRo: 'Tehnicianul vine la adresa dvs. pentru o evaluare gratuită și propune soluția optimă.',
    descRu: 'Специалист приезжает для бесплатной оценки и предлагает оптимальное решение.',
    detailRo: 'Aceeași zi',
    detailRu: 'В тот же день',
  },
  {
    number: '03',
    icon: 'ri-settings-3-line',
    titleRo: 'Instalare în 24h',
    titleRu: 'Установка за 24 часа',
    descRo: 'Instalare profesională, configurare completă a aplicației și instruire incluse. Garanție 3 ani.',
    descRu: 'Профессиональная установка, настройка приложения и обучение включены. Гарантия 3 года.',
    detailRo: 'Garanție 3 ani',
    detailRu: 'Гарантия 3 года',
  },
];

export default function HowItWorks() {
  const ref = useFadeIn<HTMLElement>();
  const { t } = useLang();

  return (
    <section id="how-it-works" ref={ref} className="section-fade py-24 bg-charcoal relative overflow-hidden">
      <div className="absolute inset-0 cyber-grid-bg opacity-25"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="slide-in-left inline-block px-4 py-1 rounded-full border border-neon/30 bg-neon/5 text-neon text-xs font-inter tracking-widest uppercase mb-4">
            {t('Cum Funcționează', 'Как это работает')}
          </span>
          <h2 className="font-rajdhani font-bold text-4xl md:text-5xl text-white">
            {t('3 Pași Simpli', '3 простых шага')}
          </h2>
          <p className="font-inter text-cyber-muted mt-3 text-base max-w-md mx-auto">
            {t(
              'De la primul mesaj la sistemul instalat — totul în 24 de ore.',
              'От первого сообщения до установленной системы — всё за 24 часа.'
            )}
          </p>
          <div className="mt-5 mx-auto w-16 h-0.5 bg-neon rounded-full line-grow"></div>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          {/* Connector */}
          <div className="hidden md:block absolute top-12 left-[22%] right-[22%] h-px bg-gradient-to-r from-neon/30 via-neon/60 to-neon/30 z-0"></div>

          {steps.map((step, idx) => (
            <div key={step.number} className="stagger-item relative flex flex-col items-center text-center group" style={{ animationDelay: `${idx * 0.15}s` }}>
              {/* Number circle */}
              <div className="relative mb-6 z-10">
                <div className="w-24 h-24 rounded-full border-2 border-neon/40 bg-charcoal-100 flex items-center justify-center group-hover:border-neon transition-all duration-300 animate-glow-pulse">
                  <span
                    className="font-rajdhani font-bold text-3xl text-neon"
                    style={{ textShadow: '0 0 10px rgba(0,255,136,0.5)' }}
                  >
                    {step.number}
                  </span>
                </div>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-neon/10 border border-neon/30">
                  <i className={`${step.icon} text-sm text-neon`}></i>
                </div>
              </div>

              {/* Card */}
              <div className="mt-4 p-6 rounded-lg border bg-charcoal-100 w-full transition-all duration-300 card-hover-glow" style={{ borderColor: 'rgba(0,255,136,0.12)' }}>
                <h3 className="font-rajdhani font-bold text-xl text-white mb-3">{t(step.titleRo, step.titleRu)}</h3>
                <p className="font-inter text-sm text-cyber-muted leading-relaxed mb-4">{t(step.descRo, step.descRu)}</p>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-neon/10 border border-neon/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-neon animate-pulse"></span>
                  <span className="font-inter text-xs text-neon">{t(step.detailRo, step.detailRu)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Banner */}
        <div className="mt-14 rounded-xl overflow-hidden border border-neon/15 relative">
          <img
            src="https://public.readdy.ai/ai/img_res/7b04de766f9f05c354d16448dba54347.jpg"
            alt={t('MyAlarm — Instalare profesionala', 'MyAlarm — Профессиональная установка')}
            className="w-full h-44 sm:h-52 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/95 via-charcoal/60 to-charcoal/95"></div>
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <div className="text-center">
              <p className="font-rajdhani font-bold text-xl sm:text-2xl md:text-3xl text-white">
                {t('Garanție 3 Ani pe Orice Instalare', 'Гарантия 3 года на любую установку')}
              </p>
              <p className="font-inter text-white/60 mt-2 text-xs sm:text-sm">
                {t('Chișinău & toată Moldova — service gratuit în garanție', 'Кишинёв и вся Молдова — бесплатный сервис в гарантийный период')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
